import React, { useEffect, useState } from 'react'
import { ArrowUp, ArrowDown, Star, PencilSquare } from 'react-bootstrap-icons'
import styled from 'styled-components'
import { ResultBox } from '../App'
import {
  StyledSideBar,
  SidebarButton
} from '../styled'
import { noselect } from '../constants'

const ENDPOINT = (process.env.REACT_APP_API_ENDPOINT || '/') + 'authors/'

const SortingPanel = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem;
  background: #f7fafc;
  border-radius: 0.5rem;
`

const Source = styled.div`
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: white;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 0.5rem 0 0;
  list-style-type: none;
`

const Li = styled.li`
  margin: 0.5rem 0;
  padding-left: 1rem;
  border-left: 2px solid #e2e8f0;
  line-height: 1.4;
`

const LinkLiSpan = styled.span`
  border-bottom: 1px solid #aaa;
`

const Link = styled.a`
  cursor: pointer;
  color: black;
`

const AuthorLink = styled(Link)`
  font-weight: 600;
  ${noselect};
`

const QuoteCountSpan = styled.span`
  margin-left: 10px;
  opacity: 0.6;
  font-size: 0.8em;
  ${noselect};
  
`

const EditIcon = styled(PencilSquare)`
  margin-left: 8px;
  opacity: 0.6;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`

const EditInput = styled.input`
  font-weight: 600;
  border: none;
  border-bottom: 2px solid #4299e1;
  background: transparent;
  padding: 2px 4px;
  margin-right: 8px;
  &:focus {
    outline: none;
  }
`

function SideBar({ onClickTitle, onClickAuthor }) {
  const [authors, setAuthors] = useState([])
  const [filters, setFilter] = useState('')
  const [editingAuthor, setEditingAuthor] = useState(null)
  const [editValue, setEditValue] = useState('')

  const handleOrdering = (e) => {
    const ordering = e.target.getAttribute('data-ordering')
    if (ordering === 'quoteCount') {
      setFilter('?orderby=quoteCount')
    } else if (ordering === 'oldestClip') {
      setFilter('?orderby=oldestClip')
    } else if (ordering === 'latestClip') {
      setFilter('?orderby=latestClip')
    }
  }

  useEffect(() => {
    const fetchAuthors = (retries) => {
      console.log(`fetching authors, retries: ${retries}`)
      fetch(
        ENDPOINT + filters,
        {
          headers: new Headers({ Authorization: 'Bearer ' + localStorage.getItem('clippingsToken') })
        }
      )
        .then(response => response.json())
        .then(data => {
          if (data === null || data === undefined || (Array.isArray(data) === true && data.length === 0)) {
            if (retries > 0) {
              fetchAuthors(retries - 1)
            } else {
              setAuthors([])
            }
          } else {
            setAuthors(data)
          }
        })
        .catch(error => {
          console.error(error)
        })
    }
    fetchAuthors(3)
  }, [filters])

  const handleEditClick = (author, e) => {
    e.stopPropagation()
    setEditingAuthor(author)
    setEditValue(author)
  }

  const handleEditSubmit = async (oldName, e) => {
    if (e.key === 'Enter' && editValue.trim() !== '') {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT || '/'}authors`, {
          method: 'PATCH',
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('clippingsToken'),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            oldName: oldName,
            newName: editValue.trim()
          })
        })

        if (response.ok) {
          setAuthors(authors.map(author => {
            if (author.author === oldName) {
              return { ...author, author: editValue.trim() }
            }
            return author
          }))
          setEditingAuthor(null)
        } else {
          console.error('failed to update author name')
        }
      } catch (error) {
        console.error('error updating author name:', error)
      }
    } else if (e.key === 'Escape') {
      setEditingAuthor(null)
    }
  }

  return (
    <StyledSideBar>
      <SortingPanel>
        <SidebarButton onClick={handleOrdering} data-ordering='latestClip'>
          <ArrowUp /> newest first
        </SidebarButton>
        <SidebarButton onClick={handleOrdering} data-ordering='oldestClip'>
          <ArrowDown /> oldest first
        </SidebarButton>
        <SidebarButton onClick={handleOrdering} data-ordering='quoteCount'>
          <Star /> most quoted authors
        </SidebarButton>
      </SortingPanel>
      <ResultBox>
        {authors.map((value, index) => (
          <Source key={index}>
            {editingAuthor === value.author ? (
              <EditInput
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => handleEditSubmit(value.author, e)}
                autoFocus
              />
            ) : (
              <>
                <AuthorLink onClick={() => onClickAuthor(value.author)}>
                  {value.author}
                  <QuoteCountSpan>
                    ({value.TotalQuoteCount} {value.TotalQuoteCount > 1 ? 'quotes' : 'quote'})
                  </QuoteCountSpan>
                </AuthorLink>
                <EditIcon
                  title="edit author name"
                  onClick={(e) => handleEditClick(value.author, e)}
                />
              </>
            )}
            <List>
              {value.books.map((item, index) => (
                <Li key={index}>
                  <Link onClick={() => onClickTitle(item)}>
                    {item}
                  </Link>
                </Li>
              ))}
            </List>
          </Source>
        ))}
      </ResultBox>
    </StyledSideBar>
  )
}

export default SideBar
